import React from "react";
import PropTypes from "prop-types";
import { Dropdown } from "@mui/base/Dropdown";
import { Menu } from "@mui/base/Menu";
import { MenuButton as BaseMenuButton } from "@mui/base/MenuButton";
import { MenuItem as BaseMenuItem, menuItemClasses } from "@mui/base/MenuItem";
import { styled } from "@mui/system";
import { CssTransition } from "@mui/base/Transitions";
import { PopupContext } from "@mui/base/Unstable_Popup";
import { useNavigate } from "react-router-dom";
import { I18n } from "@aws-amplify/core";
import { useTemplateController } from "context";

const DarkModeContext = React.createContext(false);

const SettingsMenu = () => {
  const navigate = useNavigate();
  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const handleMenuClick = (path) => {
    return () => {
      navigate(path);
    };
  };

  return (
    <DarkModeContext.Provider value={darkMode}>
      <Dropdown>
        <MenuButton>{I18n.get("Event History Settings")}</MenuButton>
        <Menu slots={{ listbox: AnimatedListbox }}>
          <MenuItem onClick={handleMenuClick("/settings/event")} style={{cursor:"pointer"}}>
            {I18n.get("Event History")}
          </MenuItem>
        </Menu>
      </Dropdown>
    </DarkModeContext.Provider>
  );
};

// Styled components and additional code
const blue = {
  50: "#F0F7FF",
  100: "#C2E0FF",
  200: "#99CCF3",
  300: "#66B2FF",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E6",
  700: "#0059B3",
  800: "#004C99",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Listbox = styled("ul")(() => {
  const darkMode = React.useContext(DarkModeContext);
  return `
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    box-sizing: border-box;
    padding: 6px;
    margin: 12px 0;
    min-width: 200px;
    border-radius: 12px;
    overflow: auto;
    outline: 0;
    background: ${darkMode ? grey[900] : "#fff"};
    border: 1px solid ${darkMode ? grey[700] : grey[200]};
    color: ${darkMode ? grey[300] : grey[900]};
    box-shadow: 0px 4px 30px ${
      darkMode ? grey[900] : grey[200]
    };
    z-index: 1;

    .closed & {
      opacity: 0;
      transform: scale(0.95, 0.8);
      transition: opacity 200ms ease-in, transform 200ms ease-in;
    }
    
    .open & {
      opacity: 1;
      transform: scale(1, 1);
      transition: opacity 100ms ease-out, transform 100ms cubic-bezier(0.43, 0.29, 0.37, 1.48);
    }

    .placement-top & {
      transform-origin: bottom;
    }

    .placement-bottom & {
      transform-origin: top;
    }
  `;
});

const AnimatedListbox = React.forwardRef(function AnimatedListbox(props, ref) {
  const { ownerState, ...other } = props;
  const popupContext = React.useContext(PopupContext);

  if (popupContext == null) {
    throw new Error(
      "The `AnimatedListbox` component cannot be rendered outside a `Popup` component"
    );
  }

  const verticalPlacement = popupContext.placement.split("-")[0];

  return (
    <CssTransition
      className={`placement-${verticalPlacement}`}
      enterClassName="open"
      exitClassName="closed"
    >
      <Listbox {...other} ref={ref} />
    </CssTransition>
  );
});

AnimatedListbox.propTypes = {
  ownerState: PropTypes.object.isRequired,
};

const MenuItem = styled(BaseMenuItem)(() => {
  const darkMode = React.useContext(DarkModeContext);
  return `
    list-style: none;
    padding: 8px;
    border-radius: 8px;
    cursor: default;
    user-select: none;

    &:last-of-type {
      border-bottom: none;
    }

    &:focus {
      outline: 3px solid ${
        darkMode ? blue[600] : blue[200]
      };
      background-color: ${
        darkMode ? grey[800] : grey[100]
      };
      color: ${darkMode ? grey[300] : grey[900]};
    }

    &.${menuItemClasses.disabled} {
      color: ${darkMode ? grey[700] : grey[400]};
    }
  `
});

const MenuButton = styled(BaseMenuButton)(() => {
  const darkMode = React.useContext(DarkModeContext);
  return `
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    padding: 8px 16px;
    border-radius: 8px;
    color: white;
    transition: all 150ms ease;
    cursor: pointer;
    background: ${darkMode ? grey[900] : "#fff"};
    border: 1px solid ${darkMode ? grey[700] : grey[200]};
    color: ${darkMode ? grey[200] : grey[900]};
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

    &:hover {
      background: ${darkMode ? grey[800] : grey[50]};
      border-color: ${darkMode ? grey[600] : grey[300]};
    }

    &:active {
      background: ${darkMode ? grey[700] : grey[100]};
    }

    &:focus-visible {
      box-shadow: 0 0 0 4px ${
        darkMode ? blue[300] : blue[200]
      };
      outline: none;
    }
  `
});

export default SettingsMenu;
